import { instantToLocalInput, localTimeCandidates } from "./local-time";
import type { GoogleCoverage } from "./google-calendar";

export type ExactInterval = { startAt: string; endAt: string };
export type AvailabilityException = {
  date: string; localStart: string | null; localEnd: string | null;
  startAt: string | null; endAt: string | null;
};
/** A private label never belongs in the derived, shareable interval projection. */
export type ExactAvailabilityContent = {
  title: string; kind: "free" | "busy"; timezone: string;
  localStart: string; localEnd: string; startAt: string; endAt: string;
  repeat: { weekdays: number[]; until: string } | null;
  exceptions: AvailabilityException[]; beforeMinutes: number; afterMinutes: number;
};
export type ExactAvailabilityRule = {
  id: string; user_id: string; revision: number; content: ExactAvailabilityContent;
  base_id: string | null;
  source_kind: "manual" | "ics" | "google" | "outlook" | "rota";
  provider_state?: GoogleCoverage | null;
  source_id: string | null; created_at: string; updated_at: string;
};
export type AvailabilityReview = { ruleId: string; date: string; reason: "clock-change" | "calendar-refresh"; localStart: string; localEnd: string };
const DAY = 86_400_000;
const datePattern = /^20\d\d-\d\d-\d\d$/;
const localPattern = /^20\d\d-\d\d-\d\dT\d\d:\d\d$/;
const record = (value: unknown): value is Record<string, unknown> => !!value && typeof value === "object" && !Array.isArray(value);
const exactKeys = (value: Record<string, unknown>, keys: string[]) => Object.keys(value).length === keys.length && keys.every(key => Object.hasOwn(value, key));
export function validAvailabilityDate(value: unknown): value is string {
  return typeof value === "string" && datePattern.test(value) && Number.isFinite(Date.parse(`${value}T00:00:00Z`)) && new Date(`${value}T00:00:00Z`).toISOString().slice(0, 10) === value;
}
export function addAvailabilityDays(date: string, days: number): string {
  if (!validAvailabilityDate(date) || !Number.isInteger(days)) throw new Error("Choose a valid calendar date.");
  return new Date(Date.parse(`${date}T00:00:00Z`) + days * DAY).toISOString().slice(0, 10);
}
function dateDistance(start: string, end: string): number { return (Date.parse(`${end}T00:00:00Z`) - Date.parse(`${start}T00:00:00Z`)) / DAY; }
function weekday(date: string): number { return new Date(`${date}T00:00:00Z`).getUTCDay() || 7; }
function validInstant(value: unknown): value is string {
  return typeof value === "string" && /^20\d\d-\d\d-\d\dT\d\d:\d\d:\d\d\.000Z$/.test(value) && Number.isFinite(Date.parse(value)) && new Date(value).toISOString() === value;
}
function validTimes(value: { localStart: unknown; localEnd: unknown; startAt: unknown; endAt: unknown }, timezone: string): boolean {
  if (typeof value.localStart !== "string" || typeof value.localEnd !== "string" || !localPattern.test(value.localStart) || !localPattern.test(value.localEnd) || !validInstant(value.startAt) || !validInstant(value.endAt)) return false;
  const span = Date.parse(value.endAt) - Date.parse(value.startAt);
  return span > 0 && span <= 48 * 3_600_000 && localTimeCandidates(value.localStart, timezone).includes(value.startAt) && localTimeCandidates(value.localEnd, timezone).includes(value.endAt);
}
export function validateExactAvailability(value: unknown): string | null {
  if (!record(value) || !exactKeys(value, ["title", "kind", "timezone", "localStart", "localEnd", "startAt", "endAt", "repeat", "exceptions", "beforeMinutes", "afterMinutes"])) return "Review the exact free-time or commitment details.";
  if (typeof value.title !== "string" || !value.title.trim() || value.title.length > 160 || !["free", "busy"].includes(String(value.kind))) return "Add a private name and choose free or busy time.";
  if (typeof value.timezone !== "string" || value.timezone.length > 80 || !value.timezone || /^[+-]/.test(value.timezone)) return "Choose the destination time zone.";
  if (!validTimes({ localStart: value.localStart, localEnd: value.localEnd, startAt: value.startAt, endAt: value.endAt }, value.timezone)) return "Review the exact local start and finish, including any clock change.";
  for (const key of ["beforeMinutes", "afterMinutes"]) if (!Number.isInteger(value[key]) || Number(value[key]) < 0 || Number(value[key]) > 1440) return "Travel and preparation buffers must be between 0 and 1,440 minutes.";
  const startDate = String(value.localStart).slice(0, 10);
  if (value.repeat !== null) {
    const repeat = value.repeat;
    if (!record(repeat) || !exactKeys(repeat, ["weekdays", "until"]) || !Array.isArray(repeat.weekdays) || !repeat.weekdays.length || repeat.weekdays.length > 7
      || repeat.weekdays.some(day => !Number.isInteger(day) || day < 1 || day > 7) || new Set(repeat.weekdays).size !== repeat.weekdays.length
      || !repeat.weekdays.includes(weekday(startDate)) || !validAvailabilityDate(repeat.until) || repeat.until < startDate || dateDistance(startDate, repeat.until) > 366) return "Choose weekly days including the first date, ending within a year.";
  }
  if (!Array.isArray(value.exceptions) || value.exceptions.length > 62 || value.repeat === null && value.exceptions.length) return "Keep up to 62 changes to a recurring week.";
  const seen = new Set<string>();
  for (const exception of value.exceptions) {
    if (!record(exception) || !exactKeys(exception, ["date", "localStart", "localEnd", "startAt", "endAt"]) || !validAvailabilityDate(exception.date) || seen.has(exception.date)
      || !record(value.repeat) || exception.date < startDate || exception.date > String(value.repeat.until) || !(value.repeat.weekdays as number[]).includes(weekday(exception.date))) return "Choose each changed date once, within the recurring week.";
    seen.add(exception.date);
    const skipped = [exception.startAt, exception.endAt, exception.localStart, exception.localEnd].every(value => value === null);
    if (!skipped && (!validTimes({ localStart: exception.localStart, localEnd: exception.localEnd, startAt: exception.startAt, endAt: exception.endAt }, value.timezone) || String(exception.localStart).slice(0, 10) !== exception.date)) return "Review the changed shift or free-time window before saving it.";
  }
  if (new TextEncoder().encode(JSON.stringify(value)).length > 20_000) return "This availability record is too large. Split it into separate weeks.";
  return null;
}

/** A local calendar day may last 23, 24 or 25 hours. Midnight can itself be skipped. */
export function availabilityDayRange(date: string, timezone: string, days = 1): ExactInterval {
  if (!validAvailabilityDate(date) || !Number.isInteger(days) || days < 1 || days > 62) throw new Error("Choose a period of one to 62 days.");
  const startOfDay = (value: string) => {
    for (let minute = 0; minute <= 180; minute++) {
      const local = `${value}T${String(Math.floor(minute / 60)).padStart(2, "0")}:${String(minute % 60).padStart(2, "0")}`;
      const found = localTimeCandidates(local, timezone)[0]; if (found) return found;
    }
    throw new Error("This local date needs review before it can be used.");
  };
  return { startAt: startOfDay(date), endAt: startOfDay(addAvailabilityDays(date, days)) };
}
function checkedRange(range: ExactInterval) {
  const start = Date.parse(range.startAt), end = Date.parse(range.endAt);
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start || end - start > 63 * DAY) throw new Error("Choose a valid availability period of up to 62 local days.");
  return { start, end };
}
type Span = { start: number; end: number };
const interval = (span: Span): ExactInterval => ({ startAt: new Date(span.start).toISOString(), endAt: new Date(span.end).toISOString() });
function merge(spans: Span[]): Span[] {
  const result: Span[] = [];
  for (const span of spans.filter(value => value.start < value.end).sort((a, b) => a.start - b.start || a.end - b.end)) {
    const last = result.at(-1);
    if (last && span.start <= last.end) last.end = Math.max(last.end, span.end); else result.push({ ...span });
  }
  return result;
}
function subtract(free: Span[], busy: Span[]): Span[] {
  let result = merge(free);
  for (const block of merge(busy)) result = result.flatMap(span => block.end <= span.start || block.start >= span.end ? [span]
    : [{ start: span.start, end: Math.min(span.end, block.start) }, { start: Math.max(span.start, block.end), end: span.end }].filter(value => value.start < value.end));
  return result;
}
/** No free rule means unknown availability, never an invented all-day window. */
export function projectExactAvailability(rules: readonly ExactAvailabilityRule[], range: ExactInterval, nowMs = Date.now()): { free: ExactInterval[]; busy: ExactInterval[]; needsReview: AvailabilityReview[]; hasFreeRules: boolean } {
  const bound = checkedRange(range);
  if (rules.length > 200) throw new Error("Too many availability rules. Narrow the period before continuing.");
  const free: Span[] = [], busy: Span[] = [], uncertainty: Span[] = [], needsReview: AvailabilityReview[] = [];
  let expanded = 0, hasFreeRules = false;
  const candidates = new Map<string, string[]>();
  const resolve = (local: string, timezone: string) => { const key = `${timezone}:${local}`; const found = candidates.get(key) ?? localTimeCandidates(local, timezone); candidates.set(key, found); return found; };
  for (const rule of rules) {
    const c = rule.content;
    if (rule.source_kind === "google") {
      const coverage = rule.provider_state;
      const starts = Date.parse(coverage?.startAt ?? ""), ends = Date.parse(coverage?.endAt ?? ""), expiry = Date.parse(coverage?.freshUntil ?? "");
      const fresh = !!coverage && typeof coverage.marker === "boolean" && [starts, ends, expiry, nowMs].every(Number.isFinite)
        && starts < ends && ends - starts <= 32 * DAY && expiry > nowMs && expiry <= nowMs + 6 * 3600000;
      const missing = fresh ? [{ start: bound.start, end: Math.min(bound.end, starts) }, { start: Math.max(bound.start, ends), end: bound.end }].filter(span => span.start < span.end) : [bound];
      if (missing.length) {
        uncertainty.push(...missing);
        needsReview.push({ ruleId: rule.id, date: c.localStart.slice(0,10), reason: "calendar-refresh", localStart: c.localStart, localEnd: c.localEnd });
      }
      if (coverage?.marker) continue;
    }
    const problem = validateExactAvailability(c); if (problem) throw new Error("A saved availability rule needs review. No free-time claim was made.");
    const first = c.localStart.slice(0, 10), dayOffset = dateDistance(first, c.localEnd.slice(0, 10));
    const from = addAvailabilityDays(instantToLocalInput(range.startAt, c.timezone).slice(0, 10), -4);
    const through = addAvailabilityDays(instantToLocalInput(range.endAt, c.timezone).slice(0, 10), 1);
    const dates: string[] = [];
    if (!c.repeat) dates.push(first);
    else for (let date = first > from ? first : from; date <= c.repeat.until && date <= through; date = addAvailabilityDays(date, 1)) if (c.repeat.weekdays.includes(weekday(date))) dates.push(date);
    for (const date of dates) {
      if (++expanded > 4000) throw new Error("Too many occurrences. Choose a shorter period.");
      const exception = c.exceptions.find(change => change.date === date);
      if (exception && exception.startAt === null) continue;
      const localStart = exception?.localStart ?? `${date}T${c.localStart.slice(11)}`;
      const localEnd = exception?.localEnd ?? `${addAvailabilityDays(date, dayOffset)}T${c.localEnd.slice(11)}`;
      const known = exception ?? (date === first ? c : null);
      const starts = known?.startAt ? [known.startAt] : resolve(localStart, c.timezone);
      const ends = known?.endAt ? [known.endAt] : resolve(localEnd, c.timezone);
      if (starts.length !== 1 || ends.length !== 1 || Date.parse(ends[0]) <= Date.parse(starts[0])) {
        let blocked = bound;
        try { blocked = checkedRange(availabilityDayRange(date, c.timezone, dayOffset + 1)); } catch { /* Unknown local boundary means the whole requested range needs review. */ }
        const uncertain = { start: blocked.start - c.beforeMinutes * 60_000, end: blocked.end + c.afterMinutes * 60_000 };
        if (uncertain.start < bound.end && uncertain.end > bound.start) {
          if (c.kind === "free") hasFreeRules = true;
          needsReview.push({ ruleId: rule.id, date, reason: "clock-change", localStart, localEnd });
          // An uncertain busy commitment cannot be treated as free. An uncertain
          // free window contributes nothing and cannot override another real one.
          if (c.kind === "busy") uncertainty.push(uncertain);
        }
        continue;
      }
      const start = Date.parse(starts[0]), end = Date.parse(ends[0]);
      if (c.kind === "free" && start < bound.end && end > bound.start) hasFreeRules = true;
      const before = c.beforeMinutes * 60_000, after = c.afterMinutes * 60_000;
      const span = c.kind === "free" ? { start: start + before, end: end - after } : { start: start - before, end: end + after };
      const clipped = { start: Math.max(bound.start, span.start), end: Math.min(bound.end, span.end) };
      if (clipped.start < clipped.end) (c.kind === "free" ? free : busy).push(clipped);
    }
  }
  return { free: subtract(free, [...busy, ...uncertainty]).map(interval), busy: merge(busy).map(interval), needsReview, hasFreeRules };
}
/** Intersect every selected member; empty/unknown availability never drops a person. */
export function commonFreeTime(members: readonly { intervals: readonly ExactInterval[]; outboundMinutes: number; returnMinutes: number }[], minimumMinutes: number): ExactInterval[] {
  if (!members.length || members.length > 50 || !Number.isInteger(minimumMinutes) || minimumMinutes < 1 || minimumMinutes > 1440) return [];
  let common: Span[] | null = null;
  for (const member of members) {
    if (member.intervals.length > 4000 || ![member.outboundMinutes, member.returnMinutes].every(value => Number.isInteger(value) && value >= 0 && value <= 1440)) return [];
    const free = merge(member.intervals.map(value => { const span = checkedRange(value); return { start: span.start + member.outboundMinutes * 60_000, end: span.end - member.returnMinutes * 60_000 }; }));
    common = common === null ? free : intersect(common, free);
    if (!common.length) return [];
  }
  return (common ?? []).filter(span => span.end - span.start >= minimumMinutes * 60_000).map(interval);
}
function intersect(left: Span[], right: Span[]): Span[] {
  let a = 0, b = 0;
  const result: Span[] = [];
  while (a < left.length && b < right.length) {
    const start = Math.max(left[a].start, right[b].start), end = Math.min(left[a].end, right[b].end);
    if (start < end) result.push({ start, end });
    const leftEnd = left[a].end, rightEnd = right[b].end;
    if (leftEnd <= rightEnd) a++;
    if (rightEnd <= leftEnd) b++;
  }
  return result;
}
